import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { integrationsAPI } from '../api';

// Where a signed-in user connects their OWN Google Apps Script links and MCP
// servers. Sections appear only for the features their plan includes; the
// server re-checks the same flags on every read and write.

const CONNECTOR_TYPES = [
  { value: 'docs', label: 'Google Docs' },
  { value: 'sheets', label: 'Google Sheets' },
  { value: 'gmail', label: 'Gmail' },
  { value: 'calendar', label: 'Calendar + Meet' }
];

const input =
  'w-full p-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white text-sm placeholder-gray-500';

const Card = ({ children, onRemove }) => (
  <div className="bg-gray-50 dark:bg-gray-800/60 p-3 rounded-lg border border-gray-200 dark:border-gray-700/60 space-y-2">
    <div className="flex justify-end -mb-1">
      <button type="button" onClick={onRemove} className="text-red-500 hover:text-red-400 text-xs">
        Remove
      </button>
    </div>
    {children}
  </div>
);

const Integrations = () => {
  const [features, setFeatures] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const f = await integrationsAPI.features();
      setFeatures(f);
      // Only ask for what the plan allows — the other call would 403 anyway.
      if (f.connectors) setConnectors(await integrationsAPI.getConnectors());
      if (f.mcp) setServers(await integrationsAPI.getMcpServers());
    } catch {
      setError('Could not load your integrations. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 4000);
  };

  const change = (setList, i, field, value) =>
    setList(list => list.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)));

  const remove = (setList, i) => setList(list => list.filter((_, idx) => idx !== i));

  const saveConnectors = async () => {
    setError(''); setSaving('connectors');
    try {
      setConnectors(await integrationsAPI.saveConnectors(connectors));
      flash('Connectors saved.');
    } catch (e) {
      setError(e.response?.data?.message || 'Could not save connectors.');
    } finally {
      setSaving('');
    }
  };

  const saveServers = async () => {
    setError(''); setSaving('mcp');
    try {
      setServers(await integrationsAPI.saveMcpServers(servers));
      flash('MCP servers saved.');
    } catch (e) {
      setError(e.response?.data?.message || 'Could not save MCP servers.');
    } finally {
      setSaving('');
    }
  };

  if (loading) {
    return <div className="p-8 text-gray-500 dark:text-gray-400">Loading integrations…</div>;
  }

  const nothingAvailable = features && !features.connectors && !features.mcp;

  return (
    // pt-16 clears the sidebar's floating toggle, which is fixed at top-left
    // and otherwise sits on top of the heading on narrow viewports.
    <div className="max-w-3xl mx-auto px-4 pt-16 pb-8">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Integrations</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Connect your own Google Apps Script links and MCP servers. Your assistant can then
        answer from them in chat.
      </p>

      {message && (
        <div className="mb-4 p-3 bg-teal-500/10 border border-teal-500/30 rounded-lg text-teal-600 dark:text-teal-300 text-sm">
          {message}
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-500 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {nothingAvailable && (
        <div className="p-6 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60 rounded-lg text-center">
          <p className="text-gray-700 dark:text-gray-300 mb-3">
            Integrations aren't included in your current plan.
          </p>
          <Link to="/subscribe" className="text-blue-500 hover:text-blue-400 font-medium">
            See plans →
          </Link>
        </div>
      )}

      {/* ---- Connectors ---- */}
      {features?.connectors && (
        <section className="mb-10">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Connectors</h2>
            <button
              type="button"
              onClick={() => setConnectors(list => [...list, {
                id: '', type: 'docs', name: '', keywords: '', url: '', token: '',
                canRead: true, canWrite: false
              }])}
              className="px-3 py-1 bg-teal-600 hover:bg-teal-700 text-white rounded text-sm"
            >
              + Add connector
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Paste the <b>/exec</b> URL of an Apps Script deployed in your own Google account.
            Keywords are the words that trigger it in chat (e.g. <i>docs, report</i>).
          </p>

          {connectors.length === 0 && (
            <p className="text-sm text-gray-500 py-2">No connectors yet.</p>
          )}

          <div className="space-y-3">
            {connectors.map((c, i) => (
              <Card key={c.id || `nc-${i}`} onRemove={() => remove(setConnectors, i)}>
                <div className="flex gap-2">
                  <select value={c.type} onChange={(e) => change(setConnectors, i, 'type', e.target.value)}
                    className={input + ' max-w-[160px]'}>
                    {CONNECTOR_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <input type="text" value={c.name} placeholder="Name"
                    onChange={(e) => change(setConnectors, i, 'name', e.target.value)} className={input} />
                </div>
                <input type="text" value={c.keywords} placeholder="keywords (comma-separated)"
                  onChange={(e) => change(setConnectors, i, 'keywords', e.target.value)} className={input} />
                <input type="url" value={c.url} placeholder="https://script.google.com/macros/s/.../exec"
                  onChange={(e) => change(setConnectors, i, 'url', e.target.value)} className={input} />
                <input type="password" autoComplete="new-password"
                  value={c.token || ''}
                  placeholder={c.hasToken ? 'Token saved — type a new one to replace' : 'Secret token (optional)'}
                  onChange={(e) => change(setConnectors, i, 'token', e.target.value)} className={input} />
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={!!c.canWrite}
                    onChange={(e) => change(setConnectors, i, 'canWrite', e.target.checked)}
                    className="accent-teal-500" />
                  Allow this connector to make changes (send, create, update)
                </label>
              </Card>
            ))}
          </div>

          <button onClick={saveConnectors} disabled={saving === 'connectors'}
            className="mt-4 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm disabled:opacity-60">
            {saving === 'connectors' ? 'Saving…' : 'Save connectors'}
          </button>
        </section>
      )}

      {/* ---- MCP servers ---- */}
      {features?.mcp && (
        <section>
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">MCP Servers</h2>
            <button
              type="button"
              onClick={() => setServers(list => [...list, { id: '', name: '', url: '', token: '' }])}
              className="px-3 py-1 bg-teal-600 hover:bg-teal-700 text-white rounded text-sm"
            >
              + Add server
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            A remote MCP endpoint your assistant can call as a tool. Must be a public
            <b> https://</b> address.
          </p>

          {servers.length === 0 && (
            <p className="text-sm text-gray-500 py-2">No MCP servers yet.</p>
          )}

          <div className="space-y-3">
            {servers.map((s, i) => (
              <Card key={s.id || `ns-${i}`} onRemove={() => remove(setServers, i)}>
                <input type="text" value={s.name} placeholder="Name (e.g. My CRM)"
                  onChange={(e) => change(setServers, i, 'name', e.target.value)} className={input} />
                <input type="url" value={s.url} placeholder="https://example.com/mcp"
                  onChange={(e) => change(setServers, i, 'url', e.target.value)} className={input} />
                <input type="password" autoComplete="new-password"
                  value={s.token || ''}
                  placeholder={s.hasToken ? 'Token saved — type a new one to replace' : 'Auth token (optional)'}
                  onChange={(e) => change(setServers, i, 'token', e.target.value)} className={input} />
              </Card>
            ))}
          </div>

          <button onClick={saveServers} disabled={saving === 'mcp'}
            className="mt-4 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm disabled:opacity-60">
            {saving === 'mcp' ? 'Saving…' : 'Save MCP servers'}
          </button>
        </section>
      )}
    </div>
  );
};

export default Integrations;
